import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getAggregatedTitleImage } from '../../../../queries/ats/common';
import { PlayData } from '../../../../queries/ats/playback/api';
import { useAsset } from '../../../../queries/ats/titles/hooks';
import { VodPlaybackOption } from '../../../../queries/ats/titles/types/playback-option';
import { EpisodeTitle, MovieTitle } from '../../../../queries/ats/titles/types/title';
import { PurchaseProductModal } from '../../../../views/ats-details/purchase-product-modal/purchase-product-modal';
import { ClientPlayer } from '../client-player/client-player';
import { PlayerLoadingSpinner } from '../player-loading-spinner';
import { getNextEpisode, getPreferredVodSource, getPrefferedSvodSource, getSourcePath } from './utils';
import { SvodSource, VodSource } from '../../../../queries/ats/titles/types/sources';

interface ClientPlayerProps {
  data: PlayData;
  title: EpisodeTitle | MovieTitle;
  playbackOption: VodPlaybackOption;
  position: number | undefined;
  reportConsumption: (position: number) => void;
  stopConsumption: (position: number) => void;
}

export function VodPlayer({ title, ...rest }: ClientPlayerProps) {
  switch (title.type) {
    case 'episode':
      return <VodEpisodePlayer episode={title} {...rest} />;
    case 'movie':
      return <ClientPlayer title={title} {...rest} />;
    default:
      return <ClientPlayer title={title} {...rest} />;
  }
}

interface VodEpisodePlayerProps extends Omit<ClientPlayerProps, 'title'> {
  episode: EpisodeTitle;
}

function VodEpisodePlayer({ episode, ...rest }: VodEpisodePlayerProps) {
  const { data: series, isLoading } = useAsset({
    id: episode.series.masterId,
    orderByServiceType: 'vod',
    orderByProviderId: undefined,
  });

  const history = useHistory();
  const { t } = useTranslation();

  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [countHasEnded, setCountHasEnded] = useState(false);

  if (isLoading) {
    return <PlayerLoadingSpinner image={getAggregatedTitleImage(episode.masterId, 'wide', 1400)} />;
  }

  if (series === undefined) {
    return <ClientPlayer title={episode} {...rest} />;
  }

  if (series.type !== 'series') {
    return <ClientPlayer title={episode} {...rest} />;
  }

  const nextEpisode = getNextEpisode(series, episode.season.sequenceNumber, episode.episode.sequenceNumber);

  if (nextEpisode === undefined) {
    return <ClientPlayer title={episode} {...rest} />;
  }

  let source: VodSource | SvodSource | undefined = undefined;

  const nextSvodSource = getPrefferedSvodSource(nextEpisode);

  if (nextSvodSource) {
    source = nextSvodSource;
  }

  const vodSource = getPreferredVodSource(nextEpisode);

  if (vodSource) {
    source = vodSource;
  }

  if (source && source.type === 'vod' && source.product.status === 'pending') {
    return (
      <Fragment>
        <PurchaseProductModal
          asset={nextEpisode}
          source={source}
          isOpen={isPurchaseOpen}
          onClose={() => {
            setIsPurchaseOpen(false);

            if (countHasEnded) {
              history.goBack();
            }
          }}
        />
        <ClientPlayer
          title={episode}
          autoplay={{
            title: t('see next episode'),
            subtitle: `${nextEpisode.titleName} - S${nextEpisode.season.sequenceNumber} E${nextEpisode.episode.sequenceNumber}`,
            image: getAggregatedTitleImage(nextEpisode.masterId, 'wide', 400),
            button: {
              label: `${t('watch')} ${t('from')} ${source.product.price.amount},-`,
              onClick: () => setIsPurchaseOpen(true),
            },
            startCountAt: 30,
            onCountEnd: () => {
              if (isPurchaseOpen) {
                setCountHasEnded(true);
              } else {
                history.goBack();
              }
            },
          }}
          {...rest}
        />
      </Fragment>
    );
  }

  if (source && (source.type === 'vod' || (source.isSubscribed && source.type === 'svod'))) {
    const path = getSourcePath(source, nextEpisode);
    const seconds = (value: number) => (value === 1 ? t('second') : t('seconds'));

    return (
      <ClientPlayer
        title={episode}
        autoplay={{
          title: (counter: number) => `${t('play next episode in')} <strong>${counter}</strong> ${seconds(counter)}`,
          subtitle: `${nextEpisode.titleName} - S${nextEpisode.season.sequenceNumber} E${nextEpisode.episode.sequenceNumber}`,
          image: getAggregatedTitleImage(nextEpisode.masterId, 'wide', 400),
          button: {
            label: `${t('watch')} ${t('episode')} ${nextEpisode.episode.sequenceNumber}`,
            onClick: () => history.replace(path),
          },
          startCountAt: 30,
          onCountEnd: () => history.replace(path),
        }}
        {...rest}
      />
    );
  }

  return null;
}
